
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { User, LogIn, LogOut, UserPlus, CloudSun, Save, Check } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { syncTasksWithSupabase } from "@/services/taskService";
import { loadTasks } from '@/utils/storage';
import { useIsMobile } from '@/hooks/use-mobile';
import { AuthError } from '@supabase/supabase-js';

export const AuthDropdown = () => {
  const { user, signIn, signUp, signOut, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [authTab, setAuthTab] = useState<'signin' | 'signup'>('signin');
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await signIn(email, password);
      setIsAuthDialogOpen(false);
      resetForm();
      
      toast({
        title: "Signed In Successfully",
        description: "Your tasks will now sync across devices",
      });
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await signUp(email, password);
      
      if (!error) {
        setIsAuthDialogOpen(false);
        resetForm();
        setAuthTab('signin');
        
        toast({
          title: "Account Created Successfully",
          description: "Please check your email for the confirmation link and then sign in.",
        });
      } else if (error.code === 'user_already_exists') {
        toast({
          title: "Account Already Exists",
          description: "This email is already registered. Please sign in instead.",
        });
        setAuthTab('signin');
      }
    } catch (error) {
      console.error("Sign up error:", error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const handleSyncTasks = async () => {
    if (!user) {
      toast({
        title: "Sign In Required",
        description: "Please sign in to sync your tasks.",
        variant: "destructive",
      });
      return;
    }

    setIsSyncing(true);
    
    try {
      const localTasks = await loadTasks();
      await syncTasksWithSupabase(localTasks);
      
      toast({
        title: "Tasks Synced",
        description: "Your tasks have been saved to your account.",
      });
    } catch (error) {
      console.error("Error syncing tasks:", error);
      toast({
        title: "Sync Failed",
        description: "There was a problem syncing your tasks. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
  };

  return (
    <div className="relative">
      {!user && (
        <div className="flex flex-col items-center">
          <Button 
            variant="outline"
            size={isMobile ? "sm" : "default"}
            className="bg-primary/10 text-primary border-primary mr-2"
            onClick={() => setIsAuthDialogOpen(true)}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {isMobile ? "Sign In" : "Save Across Devices"}
          </Button>
          
          <img 
            src="/lovable-uploads/4c5b73dc-3243-4998-915f-86f1819faafc.png" 
            alt="Clock That Logo" 
            className="h-12 opacity-90 mt-2"
          />
        </div>
      )}
    
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full w-8 h-8">
            <User className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {user ? (
            <>
              <DropdownMenuItem disabled className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="truncate">{user.email}</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="flex items-center gap-2" 
                onClick={handleSyncTasks}
                disabled={isSyncing}
              >
                <CloudSun className="h-4 w-4" />
                <span>{isSyncing ? 'Syncing...' : 'Sync Tasks'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="flex items-center gap-2" onClick={handleSignOut}>
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onSelect={() => setIsAuthDialogOpen(true)} className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              <span>Sign In / Create Account</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Save Your Tasks</DialogTitle>
            <DialogDescription>
              Create an account to access your tasks from any device.
              <div className="mt-2 flex items-start text-xs">
                <Check className="h-4 w-4 text-primary mr-2 mt-0.5" />
                <span>You can continue using the app without an account - your data will be saved on this device only.</span>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue={authTab} onValueChange={(value) => setAuthTab(value as 'signin' | 'signup')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Create Account</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-muted-foreground">
                    Password must be at least 6 characters long
                  </p>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="flex justify-center mt-4 pt-4 border-t">
            <img 
              src="/lovable-uploads/4c5b73dc-3243-4998-915f-86f1819faafc.png" 
              alt="Clock That Logo" 
              className="h-16 opacity-90"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
